import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { FitnessGoalsDataService, FitnessGoalsColumnsService } from '../../services';
import { DialogFitnessGoalFormComponent } from './fitness-goal-form/fitness-goal-form.component';
import { DialogFitnessGoalDetailsComponent } from './fitness-goal-details/fitness-goal-details.component';
import { FitnessGoal, FitnessGoalsResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-fitness-goals',
  templateUrl: './fitness-goals.component.html',
  styleUrls: ['./fitness-goals.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class FitnessGoalsComponent implements OnInit, OnDestroy {

  private fitnessGoalAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private fitnessGoalEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private fitnessGoalDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public fitnessGoalToDelete: FitnessGoal = {};
  public fitnessGoals: FitnessGoal[] = [];
  public fitnessGoal: FitnessGoal = {};
  public selectedFitnessGoals: FitnessGoal[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private fitnessGoalsDataService: FitnessGoalsDataService,
    private fitnessGoalsColumnsService: FitnessGoalsColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('FitnessGoalsComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Fitness Goals - Management');
    this.primengConfig.ripple = true;
    this.getFitnessGoals();
  }

  ngOnDestroy(): void {
    console.log('FitnessGoalsComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getFitnessGoals(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.fitnessGoalsDataService.getFitnessGoals(this.parameters).subscribe((response: FitnessGoalsResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.fitnessGoals = response.results;
        } else {
          this.fitnessGoals = this.fitnessGoals.concat(response.results);
        }
        if (response.inlineCount > this.fitnessGoals.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getFitnessGoals();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FITNESSGOALSNOTLOADED')});
    });
  }

  importFitnessGoals(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.fitnessGoalsDataService.addFitnessGoals(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('FITNESSGOALSCREATED')});
                if (proceed === j) {
                  this.getFitnessGoals();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FITNESSGOALSNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FITNESSGOALSNOTCREATED') + ' ' + msg});
            });
          } else {
            this.fitnessGoalsDataService.updateFitnessGoals(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('FITNESSGOALSUPDATED')});
                if (proceed === j) {
                  this.getFitnessGoals();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FITNESSGOALSNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('FITNESSGOALSNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.fitnessGoalsColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('fitnessGoals.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedFitnessGoals() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Fitness Goals?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.fitnessGoalsDataService.deleteFitnessGoals(this.selectedFitnessGoals).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Fitness Goals Deleted', life: 3000});
            this.getFitnessGoals();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Fitness Goals not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Fitness Goals not deleted'});
        });
        this.selectedFitnessGoals = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.fitnessGoals, this.columns, 'FitnessGoals');
  }

  exportExcel() {
    ToolsService.exportExcel(this.fitnessGoals, this.columns, 'FitnessGoals');
  }

  exportCsv() {
    ToolsService.exportCsv(this.fitnessGoals, this.columns, 'FitnessGoals');
  }

  refresh() {
    this.fitnessGoalsDataService.clearCache();
    this.getFitnessGoals();
  }

  fitnessGoalAdd(): void {
    if (!this.navigation.getParamValue('fitnessGoals.addInDialog', true)) {
      this.router.navigate(['/fitness-goals/add']);
      return;
    }
    this.fitnessGoalAddDialog = this.dialogService.open(DialogFitnessGoalFormComponent, {
      header: '', // this.translate.instant('NEWFITNESSGOAL'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        fitnessGoal: {id: null},
        users: this.fitnessGoalsColumnsService.users,
      },
    });

    this.fitnessGoalAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'FitnessGoal added'});
        this.getFitnessGoals();
      }
    });
  }

  fitnessGoalEdit(fitnessGoal: FitnessGoal): void {
    if (!this.navigation.getParamValue('fitnessGoals.editInDialog', true)) {
      this.router.navigate(['/fitness-goals/edit/' + fitnessGoal.id]);
      return;
    }
    this.fitnessGoalEditDialog = this.dialogService.open(DialogFitnessGoalFormComponent, {
      header: '', // this.translate.instant('EDITFITNESSGOAL') + ' ' + fitnessGoal.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        fitnessGoal: fitnessGoal,
        users: this.fitnessGoalsColumnsService.users,
      },
    });

    this.fitnessGoalEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'FitnessGoal updated'});
        this.getFitnessGoals();
      }
    });
  }

  fitnessGoalDetails(fitnessGoal: FitnessGoal): void {
    if (!this.navigation.getParamValue('fitnessGoals.detailsInDialog', true)) {
      this.router.navigate(['/fitness-goals/details/' + fitnessGoal.id]);
      return;
    }
    this.fitnessGoalDetailsDialog = this.dialogService.open(DialogFitnessGoalDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        fitnessGoal: fitnessGoal,
      }
    });
  }

  fitnessGoalDelete(fitnessGoal: FitnessGoal): void {
    this.fitnessGoalToDelete = fitnessGoal;
    this.messageService.clear();
    this.messageService.add({
      key: 'fitnessGoalDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + fitnessGoal.name + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.fitnessGoalsDataService.deleteFitnessGoal(this.fitnessGoalToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Fitness Goal deleted'});
      this.getFitnessGoals();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Fitness Goal not deleted'});
    });
  }

  onDeleteReject(): void {
    this.fitnessGoalToDelete = {};
    this.messageService.clear('fitnessGoalDelete');
  }

}
